import type { TimeUnit } from '@wix/ambassador-restaurants-operations-v1-operation/types';
import { DateTime } from 'luxon';

import type { TimeSlot } from '../types/businessTypes';

const MINUTES_IN_HOUR = 60;
const MS_PER_MINUTE = 6e4;

const TIME_UNIT_TO_MINUTES = {
  MINUTES: 1,
  HOURS: 60,
  DAYS: 24 * 60,
  UNKNOWN_TIME_UNIT: 0,
};

export type TimeOfDay = {
  hours?: number | null;
  minutes?: number | null;
};

export type TimeOfDayRange = {
  startTime?: TimeOfDay | null;
  endTime?: TimeOfDay | null;
};

export const getTimezoneOffset = (timeZone: string, date = new Date()) => {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));

  return (
    (tzDate.getTime() - utcDate.getTime()) / (MINUTES_IN_HOUR * MS_PER_MINUTE)
  );
};

export const toMinutes = (duration?: number, timeUnit?: TimeUnit) =>
  (duration ?? 0) * TIME_UNIT_TO_MINUTES[timeUnit ?? 'UNKNOWN_TIME_UNIT'];

export const getMinutesFromTimeOfDay = (timeOfDay: TimeOfDay) =>
  (timeOfDay.hours ?? 0) * MINUTES_IN_HOUR + (timeOfDay.minutes ?? 0);

export const formatTimeOfDayRange = ({
  range,
  locale,
}: {
  range: TimeOfDayRange;
  locale: string;
}) => {
  if (!range.startTime || !range.endTime) {
    return '';
  }

  const startTime = DateTime.fromObject({
    hour: range.startTime.hours ?? 0,
    minute: range.startTime.minutes ?? 0,
  })
    .setLocale(locale)
    .toLocaleString(DateTime.TIME_SIMPLE);
  const endTime = DateTime.fromObject({
    hour: range.endTime.hours ?? 0,
    minute: range.endTime.minutes ?? 0,
  })
    .setLocale(locale)
    .toLocaleString(DateTime.TIME_SIMPLE);

  return `${startTime} - ${endTime}`;
};

export const sortDates = (dates: string[]): string[] =>
  [...dates].sort((a, b) => a.localeCompare(b));

export const sortTimeSlots = (slots: TimeSlot[]): TimeSlot[] =>
  [...slots].sort((a, b) => a.startTime.localeCompare(b.startTime));

export const toZonedDateTime = (
  date: string | Date | undefined,
  timezone: string,
  keepLocalTime = true,
): DateTime | undefined => {
  if (!date) {
    return undefined;
  }

  const dateTime =
    date instanceof Date
      ? DateTime.fromJSDate(date, { zone: 'UTC' })
      : DateTime.fromISO(date, { zone: 'UTC' });

  const zonedDateTime = dateTime.setZone(timezone, { keepLocalTime });

  return zonedDateTime.isValid ? zonedDateTime : undefined;
};
